import { useEffect, useMemo, useRef } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import _ from 'lodash'

import {
  fetchInitialViewableDocumentsPage,
  fetchNextViewableDocumentsPage
} from '@web/helpers/fhir/viewableDocuments'
import { appointmentsKeys } from './keys'
import { APPOINTMENTS_RETRY } from './retry'

// Hard upper bound on the number of pages the auto-pagination loop will
// request. Protects against:
//   - malformed FHIR `link` arrays that keep returning a `next` relation
//   - mocked/synchronous responses where closure-captured query state
//     never gets a chance to refresh between iterations
//   - upstream retry-config changes that would otherwise let the loop
//     fire an unbounded number of requests
const MAX_DOCUMENT_PAGES = 100

const getDocumentsNextPageParam = lastPage => {
  const link = _.get(lastPage, 'link', [])
  const nextLink = _.find(link, item => _.get(item, 'relation') === 'next')
  return _.get(nextLink, 'url', undefined)
}

export const useGetViewableDocuments = ({
  patientId,
  dependentAccount = null,
  encounterIds,
  isAllowed,
  token,
  useQueryOptions = {}
}) => {
  const safeEncounterIds = encounterIds ?? []
  const hasEncounters = safeEncounterIds.length > 0

  // Content-stable identifier for the encounter set. Used as the reset-effect
  // dependency so the latch flips only when the underlying encounter set
  // actually changes — not when the caller passes a new array reference with
  // the same contents.
  const encounterSetSignature = useMemo(
    () => [...safeEncounterIds].sort().join('|'),
    [safeEncounterIds]
  )

  const documentsQueryKey = useMemo(
    () =>
      appointmentsKeys.documents(patientId, dependentAccount, safeEncounterIds),
    [patientId, dependentAccount, safeEncounterIds]
  )

  const query = useInfiniteQuery({
    queryKey: documentsQueryKey,
    queryFn: async ({ pageParam }) => {
      if (pageParam === null || pageParam === undefined) {
        return fetchInitialViewableDocumentsPage({
          patientId,
          encounter: safeEncounterIds
        })
      }
      return fetchNextViewableDocumentsPage({ nextUrl: pageParam, token })
    },
    initialPageParam: null,
    getNextPageParam: getDocumentsNextPageParam,
    enabled: Boolean(patientId) && Boolean(isAllowed) && hasEncounters,
    staleTime: 60_000,
    refetchOnMount: false,
    retryOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    ...APPOINTMENTS_RETRY,
    ...useQueryOptions
  })

  // Cancel token lives in a ref so the async loop and the cleanup share the
  // same object across renders. A local-scope cancelToken (as in the previous
  // implementation) was fine in theory but unreliable when mocked fetchers
  // resolve synchronously — React never gets to flush the cleanup before the
  // next loop iteration reads the token.
  const cancelledRef = useRef(false)
  const pagesRequestedRef = useRef(0)
  // Latch set the first time the auto-pagination loop bails on a thrown
  // error. Prevents any future effect re-entry (e.g. dataUpdatedAt ticks)
  // from re-arming the loop until the consumer explicitly refetches and we
  // reset it below.
  const stoppedOnErrorRef = useRef(false)

  useEffect(() => {
    cancelledRef.current = false
    pagesRequestedRef.current = 0
    stoppedOnErrorRef.current = false
    return () => {
      cancelledRef.current = true
    }
  }, [encounterSetSignature])

  useEffect(() => {
    // Bail out up front — no work to do.
    if (
      query.isError ||
      stoppedOnErrorRef.current ||
      !query.hasNextPage ||
      query.isFetchingNextPage ||
      query.isFetching
    ) {
      return
    }

    const fetchAllPages = async () => {
      // Read fresh state on each iteration by going through the query client
      // indirectly: we depend on the effect's deps to gate entry, but inside
      // the loop we only need to know whether to stop, and that's determined
      // by the refs and the current fetch status.
      while (
        !cancelledRef.current &&
        !query.isError &&
        !stoppedOnErrorRef.current &&
        query.hasNextPage &&
        !query.isFetchingNextPage &&
        !query.isFetching
      ) {
        if (pagesRequestedRef.current >= MAX_DOCUMENT_PAGES) {
          break
        }
        pagesRequestedRef.current += 1
        try {
          // throwOnError: true is critical — by default TanStack Query v5's
          // fetchNextPage() swallows the page-fetch rejection and resolves
          // normally, which would let this loop run forever when a mocked
          // fetcher (or a 5xx) rejects on every page.
          await query.fetchNextPage({ throwOnError: true })
        } catch (caught) {
          if (caught) {
            // swallow error - surfaced via documentsQuery.isError
          }
          stoppedOnErrorRef.current = true
          break
        }
      }
    }
    fetchAllPages()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    query.dataUpdatedAt,
    query.hasNextPage,
    query.isFetchingNextPage,
    query.isFetching,
    query.isError
  ])

  const allEntries = useMemo(
    () =>
      _.uniqBy(
        _.flatMap(_.get(query.data, 'pages', []), page =>
          _.get(page, 'entry', [])
        ),
        'resource.id'
      ),
    [query.data]
  )

  return {
    ...query,
    allEntries
  }
}
